import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { jwtDecode } from 'jwt-decode';
import { fetchPaymentHistory } from '../../../store/actions/userActions';
import { fetchEntitlement } from '../../../store/actions/entitlementActions';
import { BASE_URL } from '../../../constants';
import { SUBSCRIPTION_TIERS, isTierAvailableForRole } from '../../../config/stripeConfig';
import StripeCheckout from '../../subscription/StripeCheckout';
import { EMPTY_VALUE } from '../v2/athleteUtils';
import { Badge, Btn, Card, ConfirmModal, EmptyState, Overline, PageHeader, SegmentedControl, Tile } from '../../ForBrands/ui/BrandUI';
import Icon from '../../ForBrands/ui/icons';
import { SUPPORT_EMAIL } from '../../ForBrands/ui/mobile/support';
import { STATUS_META } from '../../ForCoaches/v2/profile/subscriptionStatus';
import { coverageKind, coverageOf, coverageSentence } from '../teamCoverage';
import '../../ForBrands/brandMode.css';

// Athlete Mode · Billing (V2 style): current plan, the Athlete Premium plan
// with a monthly / annual picker and Stripe checkout, and payment history.
// Stripe checkout comes back here with ?payment=success (and its session
// id) or ?payment=cancelled: the page says how it went, reloads the plan and
// clears the query. A plan CACHE set up (Manage tiers, a paid invoice) has
// no Stripe subscription: it shows who to contact instead of the Stripe
// controls. Premium included with the team's plan (a coach on a paid plan)
// shows as such, with no plan to buy unless the athlete has their own; an
// athlete who pays keeps their own plan and sees both.

export const ATHLETE_BILLING_PATH = '/athlete/billing';

const INTERVALS = [
  { value: 'annual', label: 'Annual' },
  { value: 'monthly', label: 'Monthly' },
];

// The positive status dot (the bm positive token) the shared status map uses.
const POSITIVE_DOT = STATUS_META.active.dot;

const PAYMENT_TONES = { success: 'outline', failed: 'critical', pending: 'attention' };

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const formatDate = (value, unix = false) =>
  new Date(unix ? value * 1000 : value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// The server keeps dates as naive UTC and DRF sends them with no zone
// ('2026-12-31T00:00:00'), which a browser would read as local time.
const ZONELESS_ISO = /^\d{4}-\d{2}-\d{2}T[\d:.]+$/;

// A date the server sent as an ISO string, a Mongo {$date}, or a unix time
// in seconds or milliseconds. A date-time with no zone is read as UTC.
// Null when it does not parse.
export const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const raw = typeof value === 'object' && !(value instanceof Date) && '$date' in value ? value.$date : value;
  let d;
  if (typeof raw === 'number') d = new Date(raw < 1e11 ? raw * 1000 : raw);
  else if (typeof raw === 'string' && ZONELESS_ISO.test(raw)) d = new Date(`${raw}Z`);
  else d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The account's own plan. The server sends it as ownEntitlement beside the
// effective entitlement, which also counts Premium included with the team's
// plan; an older answer carries only the one.
export const ownEntitlementOf = (entitlement) => entitlement?.ownEntitlement || entitlement?.entitlement || null;

// The line after the coverage sentence when the team's coverage is all the
// athlete has. A team code is no plan ("Premium is included with your team
// code."), so its line does not speak of one.
export const coveredOnlyNote = (coverage) => (coverageKind(coverage) === 'team_code'
  ? 'There is nothing to pay while it covers you.'
  : 'There is nothing to pay while that plan is active.');

// A promo code's fee waiver: Premium with no Stripe subscription behind it,
// stored as status 'trialing' until the code's end date (older grants:
// 'active' with promoCode and promoUntil). The entitlement response decides
// when it carries promoWaiver (top level, or under `subscription`); else the
// user doc from payments/history is read with the server's rule
// (entitlements.promo_waiver_grant). Returns null when this is not a waiver,
// else { until, ended }. The waiver has ended once its end date is past or
// the server already puts the account's own plan on free.
export const promoWaiverOf = (entitlement, account, now = new Date()) => {
  const span = (until) => ({ until, ended: (!!until && until < now) || ownEntitlementOf(entitlement) === 'athlete_free' });
  const told = [entitlement, entitlement?.subscription].find((src) => src && typeof src.promoWaiver === 'boolean');
  if (told) return told.promoWaiver ? span(toDate(told.promoUntil)) : null;
  if (!account || account.stripeSubscriptionId) return null;
  const sub = account.subscription || {};
  if (!sub.promoCode || !(sub.promoWaiver === true || sub.promoUntil)) return null;
  // Flagged grants are 'trialing' (or 'active'); older unflagged grants are
  // only ever 'active', so a later trial or plan on the account is not one.
  const waiverStatuses = sub.promoWaiver === true ? ['trial', 'trialing', 'active'] : ['active'];
  if (!waiverStatuses.includes(sub.status)) return null;
  // Paid for since: a later payment set the next billing date.
  const nextPayment = toDate(sub.nextPaymentDate);
  if (nextPayment && nextPayment > now) return null;
  return span(toDate(sub.promoUntil));
};

// What stands behind a paid plan: 'stripe' when the account has a Stripe
// subscription, so the Stripe portal, cancel and resume work; 'cache' when
// it has none, because CACHE set the plan up (an admin applied or granted it
// in Manage tiers, or activated it from a paid invoice) and those controls
// would only fail with "No Stripe customer found"; null until
// payments/history (`account`) has loaded, so a page shows neither the
// controls nor the note until it knows. That answer always carries the
// account's email, and its stripeSubscriptionId while Stripe keeps the
// subscription (the webhook clears it when Stripe deletes one). An invoice
// plan is known from the entitlement alone.
export const planBackingOf = (entitlement, account) => {
  if (entitlement?.invoicePayment) return 'cache';
  if (account?.stripeSubscriptionId) return 'stripe';
  return account?.email ? 'cache' : null;
};

// When a plan CACHE set up ends, or null: nothing is billed, so a grant runs
// to its trialEnd and an applied or invoiced plan to its end date (the
// server sends both as ISO strings).
export const cachePlanEnd = (entitlement, status) =>
  status === 'trialing'
    ? toDate(entitlement?.trialEnd)
    : toDate(entitlement?.subscriptionEnd) || toDate(entitlement?.nextPaymentDate);

// Shown in place of the Stripe controls on a plan CACHE set up, on the
// athlete and the coach billing pages alike.
export const CacheSetUpNote = () => (
  <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4">
    <p className="bm-body-m text-bm-ink">
      Your plan was set up by CACHE, so there is no payment method or subscription to manage here. To change or cancel it, email{' '}
      <a href={`mailto:${SUPPORT_EMAIL}`} className="text-bm-orange underline underline-offset-2">
        {SUPPORT_EMAIL}
      </a>
      .
    </p>
  </div>
);

const CheckIcon = () => (
  <span className="mt-[2px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-bm-orange text-white">
    <Icon name="check" size={11} strokeWidth={2.5} />
  </span>
);

const AthleteBillingPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [interval, setInterval] = useState('annual');
  const [refreshKey, setRefreshKey] = useState(0);

  const paymentHistory = useSelector((state) => state.user.paymentHistory);
  const entitlement = useSelector((state) => state.entitlement);
  const token = useSelector((state) => state.auth.token);
  const decoded = useMemo(() => (token ? jwtDecode(token) : { permissions: [] }), [token]);

  const availableTiers = Object.keys(SUBSCRIPTION_TIERS).filter((tierId) => isTierAvailableForRole(tierId, decoded.permissions));

  // Payment success / cancel bounce-back from Stripe checkout.
  useEffect(() => {
    const payment = new URLSearchParams(location.search).get('payment');
    if (payment === 'success') {
      toast.success('Payment successful. Your subscription is now active.');
      setRefreshKey((prev) => prev + 1);
      navigate(ATHLETE_BILLING_PATH, { replace: true });
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled. You can try again when ready.');
      navigate(ATHLETE_BILLING_PATH, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    dispatch(fetchPaymentHistory());
    dispatch(fetchEntitlement());
  }, [dispatch, refreshKey]);

  const authed = { headers: { Authorization: `Bearer ${token}` } };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/users/stripe/customer-portal/`, {}, authed);
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      console.error('Error accessing customer portal:', error);
      toast.error('Could not open the billing portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Asked in the V2 confirm modal first (Cancel subscription opens it).
  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${BASE_URL}/api/users/stripe/cancel-subscription/`, {}, authed);
      toast.success('Subscription cancelled. You keep access until the end of your billing period.');
      setConfirmCancel(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.error || 'Could not cancel the subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/api/users/stripe/resume-subscription/`, {}, authed);
      toast.success('Subscription resumed.');
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error resuming subscription:', error);
      toast.error(error.response?.data?.error || 'Could not resume the subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const status = entitlement.subscriptionStatus || null;
  const live = status === 'active' || status === 'trialing';
  // A promo code's fee waiver is stored as 'trialing' with no Stripe
  // subscription: label it as a waiver, not a trial, and hide the Stripe
  // portal and cancel controls, which need a Stripe subscription.
  const waiver = promoWaiverOf(entitlement, paymentHistory);
  const waiverLive = !!waiver && !waiver.ended && live;
  const backing = planBackingOf(entitlement, paymentHistory);
  const onPaidTier = !!entitlement.subscriptionTier && entitlement.subscriptionTier.toLowerCase() !== 'athlete_free';
  // A plan CACHE set up has ended once the server puts the account's own
  // plan on free (a grant past its trial end, an applied plan past its end
  // date), the way an ended waiver has. Only an account on a paid tier had
  // such a plan: an older free record that still says 'active' keeps its
  // status label as before.
  const cachePlanEnded = onPaidTier && backing === 'cache' && !waiver && live && ownEntitlementOf(entitlement) === 'athlete_free';
  const lapsed = (!!waiver && waiver.ended) || cachePlanEnded;
  const isPendingCancellation = status === 'canceling' || !!entitlement.cancelAtPeriodEnd;
  const isEnded = ['canceled', 'cancelled', 'expired', 'past_due', 'unpaid'].includes(status);
  const isActiveSubscription = live && !isPendingCancellation && !lapsed;
  const hasPlan = onPaidTier && (isActiveSubscription || isPendingCancellation);
  // An older waiver ('active', no Stripe) that has ended is not a plan: show
  // the plan picker so the athlete can buy.
  const hasActiveSubscription = onPaidTier && status === 'active' && !entitlement.cancelAtPeriodEnd && !lapsed;
  // The Stripe portal, cancel and resume need a Stripe subscription; a plan
  // CACHE set up says who to contact instead. Neither shows until
  // payments/history says which it is.
  const hasStripePlan = hasPlan && !waiverLive && backing === 'stripe';
  const cachePlan = hasPlan && !waiverLive && backing === 'cache';
  // Premium included with the team's plan (a coach on a paid plan). With no
  // plan of their own the athlete has nothing to buy here; one who pays
  // keeps their plan, shown as before with the coverage beside it.
  const coverage = coverageOf(entitlement);
  const coveredOnly = !!coverage && !hasPlan;
  // The plan picker, for an account with no plan, or a Stripe trial or
  // cancelled Stripe plan to restart. A plan CACHE set up runs to its end
  // date the way a fee waiver does, then the picker comes back.
  const showPlans = !coveredOnly && !hasActiveSubscription && !waiverLive && (!hasPlan || hasStripePlan);
  let statusMeta = isPendingCancellation ? STATUS_META.canceling : STATUS_META[status] || null;
  if (coveredOnly) {
    statusMeta = { label: 'Included with your team', tone: 'outline', dot: POSITIVE_DOT };
  } else if (waiverLive) {
    statusMeta = {
      label: waiver.until ? `Fee waived until ${formatDate(waiver.until)}` : 'Fee waived',
      tone: 'outline',
      dot: POSITIVE_DOT,
    };
  } else if (waiver && waiver.ended && live) {
    statusMeta = { label: 'Fee waiver ended', tone: 'passive' };
  } else if (cachePlanEnded) {
    statusMeta = { label: status === 'trialing' ? 'Trial ended' : 'Plan ended', tone: 'passive' };
  }
  const payments = paymentHistory?.subscription?.paymentHistory || [];
  const planName = entitlement.displayName || 'Athlete Premium';

  const tiles = [
    { label: 'Plan', value: hasPlan ? planName : coveredOnly ? 'Athlete Premium' : 'Free' },
    { label: 'Status', value: statusMeta ? statusMeta.label : 'Free', accent: isPendingCancellation },
  ];
  if (coveredOnly && coverage.teamName) {
    tiles.push({ label: 'Team', value: coverage.teamName });
  }
  if (hasPlan && entitlement.billingInterval && isActiveSubscription) {
    tiles.push({ label: 'Billing interval', value: entitlement.billingInterval.charAt(0).toUpperCase() + entitlement.billingInterval.slice(1) });
  }
  const cacheEnds = cachePlan ? cachePlanEnd(entitlement, status) : null;
  // A live waiver's end date is already in the status label. Stripe sends
  // its dates as unix seconds.
  if (cacheEnds) {
    tiles.push({ label: status === 'trialing' ? 'Trial ends' : 'Access ends', value: formatDate(cacheEnds), accent: status === 'trialing' });
  } else if (hasStripePlan && status === 'trialing' && entitlement.nextPaymentDate) {
    tiles.push({ label: 'Trial ends', value: formatDate(entitlement.nextPaymentDate, true), accent: true });
  } else if (hasStripePlan && entitlement.nextPaymentDate) {
    tiles.push({ label: isPendingCancellation ? 'Access ends' : 'Next billing date', value: formatDate(entitlement.nextPaymentDate, true), accent: isPendingCancellation });
  }

  let subtitle = 'Unlock CacheValue™, CacheNILx™ and brand matching with Athlete Premium.';
  if (coveredOnly) subtitle = coverageSentence(coverage);
  else if (waiverLive) subtitle = `You are on ${planName}, covered by your promo code.`;
  else if (cachePlan) subtitle = `You are on ${planName}, set up for you by CACHE.`;
  else if (hasStripePlan) subtitle = `You are on ${planName}. Manage your plan, payment method and receipts here.`;
  else if (hasPlan) subtitle = `You are on ${planName}.`;

  return (
    <div>
      <PageHeader
        eyebrow="Athlete Mode"
        title="Billing & subscription"
        subtitle={subtitle}
        actions={
          hasStripePlan ? (
            <Btn onClick={handleManageSubscription} disabled={loading}>
              {loading ? 'Opening...' : 'Manage subscription'}
            </Btn>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 pt-2">
        <Card padding="p-6" className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-3">
            <Overline>Current plan</Overline>
            {statusMeta && (
              <Badge tone={statusMeta.tone} dot={statusMeta.dot}>
                {statusMeta.label}
              </Badge>
            )}
          </div>

          {hasPlan || isEnded || coveredOnly ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {tiles.map((t) => (
                <Tile key={t.label} label={t.label} value={t.value} accent={t.accent} />
              ))}
            </div>
          ) : (
            <EmptyState action={<Btn onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}>See the plan</Btn>}>
              You are on the free plan. Athlete Premium starts with a 5-day free trial.
            </EmptyState>
          )}

          {isPendingCancellation && hasStripePlan && entitlement.nextPaymentDate && (
            <div className="flex flex-col gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="bm-body-m text-bm-ink">Your subscription is set to cancel. You keep every premium feature until {formatDate(entitlement.nextPaymentDate, true)}.</p>
              <Btn onClick={handleResumeSubscription} disabled={loading}>
                {loading ? 'Resuming...' : 'Resume subscription'}
              </Btn>
            </div>
          )}
          {isEnded && !coveredOnly && (
            <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4">
              <p className="bm-body-m text-bm-ink">Your subscription has ended. Restart Athlete Premium below to regain access.</p>
            </div>
          )}

          {coverage && (
            <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4">
              <p className="bm-body-m text-bm-ink">
                {coverageSentence(coverage)}{' '}
                {coveredOnly ? coveredOnlyNote(coverage) : 'Your own plan stays as it is.'}
              </p>
            </div>
          )}

          {waiverLive && (
            <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4">
              <p className="bm-body-m text-bm-ink">
                {waiver.until
                  ? `Your promo code covers the Athlete Premium fee until ${formatDate(waiver.until)}. There is nothing to pay until then.`
                  : 'Your promo code covers the Athlete Premium fee. There is nothing to pay.'}
              </p>
            </div>
          )}

          {cachePlan && <CacheSetUpNote />}

          {isActiveSubscription && hasStripePlan && (
            <div className="flex flex-wrap items-center gap-3 border-t border-bm-border pt-5">
              <Btn variant="secondary" onClick={handleManageSubscription} disabled={loading}>
                {loading ? 'Opening...' : 'Update payment method'}
              </Btn>
              <Btn variant="danger" onClick={() => setConfirmCancel(true)} disabled={cancelLoading}>
                {cancelLoading ? 'Cancelling...' : 'Cancel subscription'}
              </Btn>
              <span className="bm-body-s text-bm-tertiary">Invoices, receipts and payment methods live in the Stripe portal.</span>
            </div>
          )}
        </Card>

        {showPlans && (
          <div id="plans" className="flex flex-col gap-4 pt-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-col gap-1">
                <Overline>{hasPlan ? 'Your plan' : 'Choose your plan'}</Overline>
                <h2 className="bm-h-m text-bm-ink">Athlete Premium</h2>
                <p className="bm-body-m text-bm-muted">Start with a 5-day free trial. Pay yearly and save 17%.</p>
              </div>
              <SegmentedControl value={interval} onChange={setInterval} options={INTERVALS} />
            </div>

            <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
              {availableTiers.map((tierId) => {
                const tier = SUBSCRIPTION_TIERS[tierId];
                const price = interval === 'monthly' ? tier.pricing.monthly : tier.pricing.annual;
                const yearlySaving = Math.round(tier.pricing.monthly * 12 - tier.pricing.annual);
                return (
                  <Card key={tierId} padding="p-6" className="flex w-full max-w-[520px] flex-col gap-5 border-bm-orange">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-col gap-1">
                        <Overline>{tier.name}</Overline>
                        <p className="bm-body-m text-bm-muted">{tier.description}</p>
                      </div>
                      <Badge tone="brand">5-day free trial</Badge>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-baseline gap-2">
                        <span className="bm-num-l text-bm-ink">${price}</span>
                        <span className="bm-body-m text-bm-muted">per {interval === 'monthly' ? 'month' : 'year'}</span>
                      </div>
                      {interval === 'annual' && (
                        <div className="flex items-center gap-2">
                          <span className="bm-body-s text-bm-muted">${(tier.pricing.annual / 12).toFixed(2)} per month</span>
                          <Badge tone="soft">Save ${yearlySaving} a year</Badge>
                        </div>
                      )}
                    </div>
                    <StripeCheckout tier={tierId} interval={interval} label={hasPlan ? 'Restart Premium' : 'Start free trial'} />
                    <div className="border-t border-bm-border" />
                    <ul className="flex flex-col gap-[10px]">
                      {tier.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-[10px] bm-body-m text-bm-ink">
                          <CheckIcon />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="bm-body-s text-bm-tertiary">Secured by Stripe. Cancel any time from this page.</p>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        <Card padding="p-0" className="overflow-hidden">
          <div className="flex items-center justify-between px-6 pt-5 pb-4">
            <div className="flex flex-col gap-1">
              <Overline>Payment history</Overline>
              <p className="bm-body-s text-bm-muted">Every charge on your account.</p>
            </div>
          </div>
          {payments.length === 0 ? (
            <div className="px-6 pb-6">
              <EmptyState>No payments yet.</EmptyState>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-bm-subtle">
                    <th className="bm-th pl-6 text-left">Date</th>
                    <th className="bm-th text-left">Amount</th>
                    <th className="bm-th text-left">Billing cycle</th>
                    <th className="bm-th text-left">Status</th>
                    <th className="bm-th pr-6 text-left">Billing address</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr key={index} className="border-t border-bm-border">
                      <td className="bm-td pl-6 bm-body-m text-bm-ink">{payment.date ? new Date(payment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : EMPTY_VALUE}</td>
                      <td className="bm-td bm-num-m text-bm-ink">{money(payment.amount)}</td>
                      <td className="bm-td bm-body-m text-bm-muted">{payment.billingCycle || 'One-time'}</td>
                      <td className="bm-td">
                        <Badge tone={PAYMENT_TONES[payment.status] || 'passive'} dot={payment.status === 'success' ? POSITIVE_DOT : undefined} className="capitalize">
                          {payment.status || 'unknown'}
                        </Badge>
                      </td>
                      <td className="bm-td pr-6 bm-body-s text-bm-muted">{payment.billingAddress || EMPTY_VALUE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {confirmCancel && (
        <ConfirmModal
          title="Cancel Athlete Premium?"
          body="Your plan stops renewing. You keep every premium feature until the end of the current billing period."
          ackLabel="I understand my plan ends at the end of this billing period."
          confirmLabel="Cancel subscription"
          tone="danger"
          busy={cancelLoading}
          onConfirm={handleCancelSubscription}
          onClose={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );
};

export default AthleteBillingPage;
